import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CpuChipIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Droplet, Thermometer, Gauge } from 'lucide-react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { tempUnit } from '../utils/temperatureUnit';
import { isDM510 } from '../utils/modelLabel';

// Channel colours, taken from the panel so the same reading is the same colour
// on the machine and here.
const INLET_COLOR = '#5cb85c';
const OUTLET_COLOR = '#428bca';
const OUTLET2_COLOR = '#17a2b8';
const DRYING_COLOR = '#d9534f';
const RATE_COLOR = '#f0ad4e';

// --- Sub-Components ---

const fmtVal = (v) => {
  if (v === '--' || v === null || v === undefined) return '--';
  if (typeof v === 'number') return v.toFixed(2);
  return v;
};

const formatVdc = (vdc) => {
  const voltage = typeof vdc === 'number' ? vdc : parseFloat(vdc) || 0;
  return voltage.toFixed(2);
};

// The bar carries no text. On the panel the voltage sits beside the reading
// above it, not inside the bar.
const VoltageBar = ({ vdc, color }) => {
  const voltage = typeof vdc === 'number' ? vdc : parseFloat(vdc) || 0;
  const pct = Math.min(Math.max((voltage / 10) * 100, 0), 100);
  return (
    <div className="h-5 bg-gray-200 rounded-xl overflow-hidden">
      <div
        className="h-full rounded-xl transition-all duration-500"
        style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}cc 0%, ${color} 100%)` }}
      />
    </div>
  );
};

// One reading: its name, then the scaled value on the left with the raw
// voltage on the right, then the bar underneath.
const ParameterSection = ({ label, value, unit, vdc, color }) => (
  <div className="bg-gray-50 rounded-lg p-2 space-y-0.5">
    <div className="text-sm font-semibold text-gray-600">{label}</div>
    <div className="flex items-baseline justify-between">
      <span className="text-2xl font-bold text-gray-800">
        {fmtVal(value)}{unit}
      </span>
      <span className="text-lg font-semibold" style={{ color }}>
        {formatVdc(vdc)}V
      </span>
    </div>
    <VoltageBar vdc={vdc} color={color} />
  </div>
);

// Combined card for Inlet / Outlet (moisture + temperature in one card).
// `toggle` is rendered in the header and is how the second outlet sensor
// appears when one is fitted.
const SensorGroupCard = ({ label, icon: Icon, color, rows, toggle }) => (
  <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm bg-white">
    <div className="px-2.5 py-1.5 flex items-center gap-1.5" style={{ background: color }}>
      <div className="w-8 h-8 rounded-md bg-white/20 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-white text-[17px] font-semibold capitalize">{label}</span>
      {toggle}
    </div>
    <div className="p-2 space-y-2">
      {rows.map((row) => (
        <ParameterSection key={row.label} {...row} color={color} />
      ))}
    </div>
  </div>
);

// Single-value card for Drying Temperature / Rate Input / Rate Output
const SingleGaugeCard = ({ label, icon: Icon, color, vdc, value, unit }) => (
  <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm bg-white">
    <div className="px-2.5 py-1.5 flex items-center gap-1.5" style={{ background: color }}>
      <div className="w-8 h-8 rounded-md bg-white/20 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-white text-base font-semibold">{label}</span>
    </div>
    <div className="p-2 space-y-0.5">
      <div className="flex items-baseline justify-between">
        <span className="text-2xl font-bold text-gray-800">
          {fmtVal(value)}{unit}
        </span>
        <span className="text-lg font-semibold" style={{ color }}>
          {formatVdc(vdc)}V
        </span>
      </div>
      <VoltageBar vdc={vdc} color={color} />
    </div>
  </div>
);

const DigitalCard = ({ label, state }) => {
  const isOn = state === 'on' || state === true || state === 1;
  const isFlashing = state === 'flashing';

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 flex flex-col items-center gap-2">
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center ${
          isOn
            ? 'bg-green-400 shadow-[0_0_16px_rgba(74,222,128,0.5)]'
            : isFlashing
            ? 'bg-yellow-400 shadow-[0_0_16px_rgba(250,204,21,0.5)] animate-pulse'
            : 'bg-gray-300'
        }`}
      >
        <div
          className={`w-6 h-6 rounded-full ${
            isOn ? 'bg-green-200' : isFlashing ? 'bg-yellow-200' : 'bg-gray-200'
          }`}
        />
      </div>
      <span className="text-sm text-gray-600 text-center leading-tight">{label}</span>
      <span
        className={`text-sm font-bold ${
          isOn ? 'text-green-600' : isFlashing ? 'text-yellow-600' : 'text-gray-400'
        }`}
      >
        {isFlashing ? 'FLASH' : isOn ? 'ON' : 'OFF'}
      </span>
    </div>
  );
};

// Dryer State is derived from the four flags below it, so the panel reads it
// as a headline banner above the grid rather than as a fifth lamp.
const DryerStateBanner = ({ value }) => {
  const state = String(value || 'standby').toLowerCase();
  let color = '#6b7280';
  let lit = false;
  if (state === 'running' || state === 'unloading') { color = '#10b981'; lit = true; }
  else if (state === 'idle_run' || state === 'preheat') { color = '#f59e0b'; lit = true; }
  else if (state === 'primed') { color = '#3b82f6'; lit = true; }

  return (
    <div className="flex items-center gap-3 rounded-2xl border-2 border-gray-200 bg-white px-6 py-4">
      <div
        className={`w-5 h-5 rounded-full shrink-0 ${lit ? 'animate-pulse' : ''}`}
        style={{ background: color, boxShadow: lit ? `0 0 14px ${color}` : 'none' }}
      />
      <span className="text-lg font-semibold text-gray-700">Dryer State:</span>
      <span className="text-2xl font-bold uppercase" style={{ color }}>
        {state.replace('_', ' ')}
      </span>
    </div>
  );
};

// One card per flag, the same lamp look as the Digital Inputs and Outputs
// pages: a lit ring in the status colour, the name, then the state text.
const DryerStatusCard = ({ label, value, type }) => {
  const isBoolTrue = value === true || value === 'true' || value === 1 || value === 'on';

  let color = '#6b7280';
  let statusText = 'OFF';
  let lit = false;

  if (type === 'dryer_full') {
    if (isBoolTrue) { color = '#10b981'; statusText = 'FULL'; lit = true; }
    else { statusText = 'NOT FULL'; }
  } else if (isBoolTrue) {
    color = '#10b981';
    statusText = 'ON';
    lit = true;
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 flex flex-col items-center gap-2">
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center ${lit ? 'animate-pulse' : ''}`}
        style={{ background: color, boxShadow: lit ? `0 0 16px ${color}80` : 'none' }}
      >
        <div className="w-6 h-6 rounded-full bg-white/40" />
      </div>
      <span className="text-sm text-gray-600 text-center leading-tight">{label}</span>
      <span className="text-sm font-bold" style={{ color }}>{statusText}</span>
    </div>
  );
};

// --- Main Component ---

const SupportDisplay = ({ deviceSerial, onClose, model = '' }) => {
  const { getTopicData, subscribe, unsubscribe, isSubscribed, connectionState } = useWebSocket();
  // A DM510's panel has none of the 2500i's I/O, zone or dryer-status signals on
  // its Modbus map. Its Support is one page read off the live feed the home view
  // uses; it streams no diagnostic feed, so none is asked for.
  const is510 = isDM510(model);
  const [diagnosticsData, setDiagnosticsData] = useState(null);
  const [homeData, setHomeData] = useState(null);
  const [activeCategory, setActiveCategory] = useState(is510 ? 'dm510' : 'io_diagnostics');
  const [activeSubTab, setActiveSubTab] = useState('analog_in');
  const [activeOutlet, setActiveOutlet] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The readings come from the diagnostic feed. Whether a second outlet sensor
  // is fitted at all is only published on the home feed, so this page needs
  // both — the panel does the same thing.
  //
  // Subscriptions are not counted per viewer, so on the way out we drop only
  // the topics we opened ourselves. Releasing one the home view had already
  // claimed would leave that view with no data.
  //
  // isSubscribed is rebuilt every time anything subscribes, so naming it as a
  // dependency would tear this effect down and set it up again on its own
  // result, forever. The socket helpers are read through a ref instead, and
  // the effect runs once per device.
  const ws = useRef(null);
  ws.current = { subscribe, unsubscribe, isSubscribed };

  const claimedTopics = useRef([]);
  useEffect(() => {
    if (!deviceSerial) return;
    claimedTopics.current = (is510 ? ['home'] : ['diagnostic', 'home']).filter(
      (topic) => !ws.current.isSubscribed(deviceSerial, topic)
    );
    claimedTopics.current.forEach((topic) => ws.current.subscribe(deviceSerial, topic));
    return () => {
      claimedTopics.current.forEach((topic) => ws.current.unsubscribe(deviceSerial, topic));
      claimedTopics.current = [];
    };
  }, [deviceSerial, is510]);

  // Poll for data updates
  useEffect(() => {
    const interval = setInterval(() => {
      const topicData = getTopicData(deviceSerial, 'diagnostic');
      if (topicData?.data) {
        setDiagnosticsData(topicData.data);
        setLoading(false);
      }
      const home = getTopicData(deviceSerial, 'home');
      if (home?.data?.live) {
        setHomeData(home.data.live);
        if (is510) setLoading(false);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [getTopicData, deviceSerial, is510]);

  // --- Data Helpers ---

  const findValue = (name, limit = 2) => {
    if (!diagnosticsData) return '--';
    const item = diagnosticsData.find((d) => d.name === name);
    if (!item) return '--';
    const value = item.value;
    if (value === null || value === undefined) return '--';
    if (typeof value === 'number') return value.toFixed(limit);
    return value;
  };

  const findRawValue = (name) => {
    if (!diagnosticsData) return null;
    const item = diagnosticsData.find((d) => d.name === name);
    return item?.value ?? null;
  };

  // Whichever unit the panel is set to, the diagnostic feed carries the flag
  // alongside the readings, so these pages label rather than convert.
  const unit = tempUnit(findRawValue('temperature_unit'));

  // The home feed arrives either as a list of named values or as a plain
  // object, depending on the device, so read it the way the home view does.
  const findHomeValue = (name) => {
    if (!homeData) return undefined;
    if (Array.isArray(homeData)) return homeData.find((d) => d.name === name)?.value;
    return homeData[name];
  };

  // A second outlet sensor is optional. With none fitted the Outlet card looks
  // exactly as it always has; with one, it gains a toggle rather than a second
  // card, which is how the panel shows it.
  const outlet2Enabled = findHomeValue('outlet_2_enabled') === true;
  const showingOutlet2 = outlet2Enabled && activeOutlet === 2;

  const modelStateLabel = () => {
    const state = findRawValue('model_state');
    return { waiting_to_initialize: 'Initializing', learning: 'Learning', ready: 'Ready' }[state] || state || '--';
  };

  const getArrayValue = (name, index, limit = 2) => {
    if (!diagnosticsData) return '--';
    const item = diagnosticsData.find((d) => d.name === name);
    if (!item || !Array.isArray(item.value)) return '--';
    const value = item.value[index];
    if (value === null || value === undefined) return '--';
    if (typeof value === 'number') return value.toFixed(limit);
    return value;
  };

  const formatTime = (name) => {
    const raw = findRawValue(name);
    if (raw === null || raw === undefined || raw === '--') return '--';
    const totalSec = Math.round(Number(raw));
    if (isNaN(totalSec) || totalSec < 0) return '--';
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${totalSec}s`;
  };

  // --- Category Definitions ---

  // A number from the home feed, or null when the feed has none.
  const homeNumber = (name) => {
    const value = findHomeValue(name);
    return value === null || value === undefined || value === '' || isNaN(Number(value)) ? null : Number(value);
  };

  // The DM510's single page: its dryer state, the Ready and Learn lights of the
  // panel's mode group, why Automatic is not available, and the model's
  // predicted moisture and suggested rate, in the 2500i pages' own cards.
  const dm510Categories = {
    dm510: {
      title: 'Control Diagnostics',
      render: () => {
        const reasonCode = homeNumber('not_ready_reason');
        const reasonText = findHomeValue('not_ready_text');
        const reason =
          reasonCode === null ? '--'
          : reasonCode === 0 ? 'None'
          : reasonText ? String(reasonText).replace(/^./, (c) => c.toUpperCase())
          : `Code ${reasonCode}`;
        const predicted = homeNumber('predicted_moisture');
        const suggested = homeNumber('suggested_rate');
        return (
          <div className="space-y-5 max-w-4xl">
            <DryerStateBanner value={findHomeValue('v2_dryer_state')} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <DigitalCard label="Ready Light" state={findHomeValue('ready')} />
              <DigitalCard label="Learn Light" state={findHomeValue('learn')} />
              <div className="col-span-2 rounded-xl border border-gray-200 bg-white shadow-sm p-4 flex flex-col justify-center">
                <span className="text-sm text-gray-500 uppercase tracking-wider">Not Ready Reason</span>
                <span className="text-2xl font-bold text-gray-800 mt-1">{reason}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl overflow-hidden shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-[#0961ad] to-[#0b7dda] px-5 py-3 flex items-center justify-between">
                  <span className="text-white font-bold">Predicted Moisture</span>
                  <span className="text-xs font-bold bg-green-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">Live</span>
                </div>
                <div className="bg-white p-5 text-center">
                  <div className="text-5xl font-bold font-mono text-[#0961ad]">
                    {predicted === null ? '--' : predicted.toFixed(2)}
                    {predicted !== null && <span className="text-xl text-gray-400 ml-1">%</span>}
                  </div>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-[#d97706] to-[#f59e0b] px-5 py-3 flex items-center justify-between">
                  <span className="text-white font-bold">Suggested Rate</span>
                  <span className="text-xs font-bold bg-green-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">Live</span>
                </div>
                <div className="bg-white p-5 text-center">
                  <div className="text-5xl font-bold font-mono text-[#d97706]">
                    {/* Zero from the panel means no suggestion, as on the 2500i */}
                    {suggested !== null && suggested > 0 ? suggested.toFixed(2) : '--'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
  };

  const categories = is510 ? dm510Categories : {
    io_diagnostics: {
      title: 'IO Diagnostics',
      tabs: [
        {
          id: 'analog_in',
          title: 'Analog Inputs',
          color: '#3b82f6',
          render: () => (
            <div className="space-y-4">
              {/* Row 1: Inlet + Outlet combined cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SensorGroupCard
                  label="Inlet"
                  icon={Droplet}
                  color={INLET_COLOR}
                  rows={[
                    { label: 'Moisture', vdc: findRawValue('inlet_moisture_vdc'), value: findValue('inlet_moisture'), unit: '%' },
                    { label: 'Temperature', vdc: findRawValue('inlet_temperature_vdc'), value: findValue('inlet_temperature'), unit },
                  ]}
                />
                <SensorGroupCard
                  label={showingOutlet2 ? 'Outlet 2' : 'Outlet'}
                  icon={Droplet}
                  color={showingOutlet2 ? OUTLET2_COLOR : OUTLET_COLOR}
                  toggle={outlet2Enabled && (
                    <div className="ml-auto flex bg-white/15 rounded-md p-0.5">
                      {[1, 2].map((n) => (
                        <button
                          key={n}
                          onClick={() => setActiveOutlet(n)}
                          className={`px-2.5 py-0.5 rounded text-[13px] font-bold transition-colors ${
                            activeOutlet === n ? 'bg-white/95' : 'text-white/80 hover:text-white'
                          }`}
                          style={activeOutlet === n ? { color: n === 2 ? OUTLET2_COLOR : OUTLET_COLOR } : undefined}
                        >
                          {n === 2 ? 'OUTLET 2' : 'OUTLET'}
                        </button>
                      ))}
                    </div>
                  )}
                  rows={[
                    {
                      label: 'Moisture',
                      vdc: findRawValue(showingOutlet2 ? 'outlet_2_moisture_vdc' : 'outlet_moisture_vdc'),
                      value: findValue(showingOutlet2 ? 'outlet_2_moisture' : 'outlet_moisture'),
                      unit: '%',
                    },
                    {
                      label: 'Temperature',
                      vdc: findRawValue(showingOutlet2 ? 'outlet_2_temperature_vdc' : 'outlet_temperature_vdc'),
                      value: findValue(showingOutlet2 ? 'outlet_2_temperature' : 'outlet_temperature'),
                      unit,
                    },
                  ]}
                />
              </div>
              {/* Row 2: Drying Temperature + Rate Input */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SingleGaugeCard label="Drying Temperature" icon={Thermometer} color={DRYING_COLOR} vdc={findRawValue('drying_temperature_vdc')} value={findValue('drying_temperature')} unit={unit} />
                <SingleGaugeCard label="Rate Input" icon={Gauge} color={RATE_COLOR} vdc={findRawValue('discharge_rate_in_vdc')} value={findValue('discharge_rate_in')} unit="" />
              </div>
            </div>
          ),
        },
        {
          id: 'analog_out',
          title: 'Analog Outputs',
          color: '#8b5cf6',
          render: () => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SingleGaugeCard label="Rate Output" icon={Gauge} color={RATE_COLOR} vdc={findRawValue('discharge_rate_out_vdc')} value={findValue('discharge_rte_out')} unit="" />
            </div>
          ),
        },
        {
          id: 'digital_in',
          title: 'Digital Inputs',
          color: '#10b981',
          render: () => (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <DigitalCard label="Local/Remote" state={findRawValue('local_remote')} />
              <DigitalCard label="Outlet Calibration" state={findRawValue('outlet_calibration_button')} />
              <DigitalCard label="Fan Status Input" state={findRawValue('fan_status_input')} />
              <DigitalCard label="Outlet 2 Calibration" state={findRawValue('outlet_2_calibration_button')} />
            </div>
          ),
        },
        {
          id: 'digital_out',
          title: 'Digital Outputs',
          color: '#ef4444',
          render: () => (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <DigitalCard label="Local/Remote Output" state={findRawValue('local_remote_digital_out')} />
              <DigitalCard label="Outlet Calibration Lamp" state={findRawValue('outlet_calibration_lamp')} />
              <DigitalCard label="Outlet 2 Calibration Lamp" state={findRawValue('outlet_2_calibration_lamp')} />
              <DigitalCard label="Remote Alarm Output" state={findRawValue('alarm_lamp')} />
            </div>
          ),
        },
      ],
    },
    control_diagnostics: {
      title: 'Control Diagnostics',
      render: () => {
        return (
          <div className="space-y-4">
            {/* Operator View - Two main cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Moisture Control Card */}
              <div className="rounded-xl overflow-hidden shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-[#0961ad] to-[#0b7dda] px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">Moisture Control</span>
                  </div>
                  <span className="text-xs font-bold bg-green-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">Live</span>
                </div>
                <div className="bg-white p-5">
                  <div className="text-center mb-4">
                    <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">Moisture Setpoint</div>
                    <div className="text-5xl font-bold font-mono text-[#0961ad]">
                      {findValue('moisture_setpoint', 2)}
                      <span className="text-xl text-gray-400 ml-1">%</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">Current Outlet</div>
                      <div className="text-3xl font-bold font-mono text-[#0961ad]">
                        {findValue('outlet_moisture', 2)}
                        <span className="text-sm text-gray-400 ml-0.5">%</span>
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">Predicted Outlet</div>
                      <div className="text-3xl font-bold font-mono text-[#0961ad]">
                        {getArrayValue('moisture_predicted_zone', 2, 2)}
                        <span className="text-sm text-gray-400 ml-0.5">%</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center mt-3">
                    <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">Model State</div>
                    <div className="text-2xl font-bold text-[#0961ad]">{modelStateLabel()}</div>
                  </div>
                </div>
              </div>

              {/* Discharge Rate Card */}
              <div className="rounded-xl overflow-hidden shadow-sm border border-gray-200">
                <div className="bg-gradient-to-r from-[#d97706] to-[#f59e0b] px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">Discharge Rate</span>
                  </div>
                  <span className="text-xs font-bold bg-green-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">Live</span>
                </div>
                <div className="bg-white p-5">
                  <div className="text-center mb-4">
                    <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">Current Rate</div>
                    <div className="text-5xl font-bold font-mono text-[#d97706]">
                      {findValue('discharge_rate', 2)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">Setpoint</div>
                      <div className="text-3xl font-bold font-mono text-[#d97706]">
                        {findValue('discharge_rate_setpoint', 0)}
                      </div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <div className="text-sm text-gray-400 uppercase tracking-wider mb-1">Suggested</div>
                      <div className="text-3xl font-bold font-mono text-[#d97706]">
                        {/* Zero from the controller means no suggestion (model not initialized or dryer not running) */}
                        {findRawValue('suggested_rate') > 0 ? findValue('suggested_rate', 2) : '--'}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Time in dryer bar */}
                <div className="bg-purple-50 border-t border-purple-200 px-5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">&#x23F1;</span>
                    <span className="text-sm font-bold text-purple-700 uppercase">Product Time in Dryer</span>
                  </div>
                  <span className="text-base font-bold font-mono text-purple-600">{formatTime('time_in_dryer')}</span>
                </div>
              </div>
            </div>

            {/* The panel carries the betas as a plain footer row along the
                bottom of the page rather than in a card of their own. */}
            <div className="flex flex-wrap gap-x-10 gap-y-2 border-t border-gray-200 pt-3">
              {[
                ['Beta 1', findValue('beta_1', 10)],
                ['Beta 2', findValue('beta_2', 10)],
              ].map(([label, val]) => (
                <div key={label} className="flex items-baseline gap-2">
                  <span className="text-sm text-gray-500">{label}</span>
                  <span className="text-sm font-bold font-mono text-gray-800">{val}</span>
                </div>
              ))}
            </div>
          </div>
        );
      },
    },
    model_overview: {
      title: 'Model Overview',
      render: () => {
        const modelState = findRawValue('model_state');
        const stateLabel = modelStateLabel();
        const stateColors = {
          learning: { header: 'bg-amber-400', body: 'bg-amber-500' },
          ready: { header: 'bg-emerald-400', body: 'bg-emerald-500' },
        }[modelState] || { header: 'bg-gray-400', body: 'bg-gray-500' };
        return (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-[#1e3a5f] to-[#2a5298] text-white">
                  <th className="px-4 py-3 text-center text-base font-semibold">Zone</th>
                  <th className="px-4 py-3 text-center text-base font-semibold">% Full</th>
                  <th className="px-4 py-3 text-center text-base font-semibold">Average entrance moisture</th>
                  <th className="px-4 py-3 text-center text-base font-semibold">Predicted average moisture</th>
                  <th className="px-4 py-3 text-center text-base font-semibold">Predicted exit moisture</th>
                </tr>
              </thead>
              <tbody>
                {['Garner', 'Hot', 'Cold'].map((zone, i) => {
                  const key = zone.toLowerCase();
                  return (
                    <tr key={zone} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-center text-base font-medium">{zone}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-green-50 text-green-800 font-mono text-base px-3 py-1 rounded">
                          {findValue(`${key}_zone_percent_full`)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-green-50 text-green-800 font-mono text-base px-3 py-1 rounded">
                          {findValue(`${key}_zone_average_entrance_moisture`)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-green-50 text-green-800 font-mono text-base px-3 py-1 rounded">
                          {findValue(`${key}_zone_average_moisture`)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="bg-green-50 text-green-800 font-mono text-base px-3 py-1 rounded">
                          {findValue(`${key}_zone_exit_moisture`)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-xl overflow-hidden shadow-sm">
              <div className="bg-green-400 px-4 py-2 text-center font-semibold text-white text-base">Inlet Moisture</div>
              <div className="bg-green-500 px-4 py-5 text-center">
                <span className="text-5xl font-bold font-mono text-white">{findValue('inlet_moisture_control')}</span>
                <span className="text-white/70 text-xl ml-1">%</span>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-sm">
              <div className="bg-blue-400 px-4 py-2 text-center font-semibold text-white text-base">Outlet Moisture</div>
              <div className="bg-blue-500 px-4 py-5 text-center">
                <span className="text-5xl font-bold font-mono text-white">{findValue('outlet_moisture_control')}</span>
                <span className="text-white/70 text-xl ml-1">%</span>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-sm">
              <div className={`${stateColors.header} px-4 py-2 text-center font-semibold text-white text-base`}>Model State</div>
              <div className={`${stateColors.body} px-4 py-5 text-center`}>
                <span className="text-4xl leading-[3rem] font-bold text-white">{stateLabel}</span>
              </div>
            </div>
          </div>
        </div>
        );
      },
    },
    dryer_status: {
      title: 'Dryer Status',
      render: () => (
        <div className="space-y-5 max-w-3xl">
          <DryerStateBanner value={findRawValue('dryer_state')} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <DryerStatusCard label="Dryer Full Status" value={findRawValue('dryer_full_status')} type="dryer_full" />
            <DryerStatusCard label="Fan Status" value={findRawValue('fan_status')} type="fan" />
            <DryerStatusCard label="Burner Status" value={findRawValue('burner_status')} type="burner" />
            <DryerStatusCard label="Discharge Status" value={findRawValue('discharge_status_state')} type="discharge" />
          </div>
        </div>
      ),
    },
  };

  // --- Render Helpers ---

  const renderContent = () => {
    const category = categories[activeCategory];
    if (!category) return null;

    if (category.tabs) {
      const currentTab = category.tabs.find((t) => t.id === activeSubTab);
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-3">
            {category.tabs.map((tab) => {
              const active = activeSubTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id)}
                  className={`py-3 px-4 rounded-[10px] font-semibold text-sm transition-all border-2 ${
                    active
                      ? 'text-white shadow-lg border-white/30'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                  style={active ? { background: tab.color, boxShadow: `0 4px 12px ${tab.color}40` } : undefined}
                >
                  {tab.title.toUpperCase()}
                </button>
              );
            })}
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSubTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {currentTab?.render()}
            </motion.div>
          </AnimatePresence>
        </div>
      );
    }
    return category.render();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-gray-900/95 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-100 rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-900 to-primary-700 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <CpuChipIcon className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">Support</h2>
              <p className="text-primary-200 text-sm">Device: {deviceSerial}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <XMarkIcon className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Connection Warning */}
        {connectionState !== 'connected' && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-2 flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">Live link lost — reconnecting. Values shown may be stale.</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 border-r border-gray-300 overflow-y-auto">
            <div className="space-y-0">
              {Object.entries(categories).map(([key, category]) => (
                <button
                  key={key}
                  onClick={() => {
                    setActiveCategory(key);
                    if (key === 'io_diagnostics') setActiveSubTab('analog_in');
                  }}
                  className={`w-full text-left px-5 py-4 transition-all border-b border-gray-200 ${
                    activeCategory === key
                      ? 'bg-gradient-to-r from-primary-900 to-primary-700 text-white shadow-lg'
                      : 'hover:bg-primary-50 text-gray-700 hover:text-primary-900'
                  }`}
                >
                  <span className="font-medium">{category.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <ArrowPathIcon className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-600">Loading diagnostics data...</p>
                  <p className="text-sm text-gray-400 mt-2">
                    If this doesn't load within a few seconds, the dryer's cloud link may be down.
                  </p>
                </div>
              </div>
            ) : (is510 ? homeData : diagnosticsData) ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="max-w-6xl mx-auto"
                >
                  {renderContent()}
                </motion.div>
              </AnimatePresence>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                  <p className="text-gray-600">No diagnostics data available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SupportDisplay;
